// This module owns live note lifecycle on the keyboard surface.
// It handles note-on/off transitions after coords are already resolved,
// including visual pressed-state redraws, sustain/latch behavior, modulation
// takeover handoff, and wheel/bulk-refresh bookkeeping tied to those note
// lifecycles. It does not resolve raw input addresses or mutate modulation
// history directly; callers provide a Keys-like runtime object.

import {
  addSustainedHex,
  clearSustainedHexes,
  removeSustainedHex,
} from "./sounding-note-runtime.js";
import {
  createTransferredHex,
  releaseTransferredSourceExpression,
  shouldSuppressTransferredSourceRelease,
} from "./note-transfer-runtime.js";
import { frameForNewNotes } from "../tuning/modulation-runtime.js";
import { scalaToCents } from "../settings/scale/parse-scale";
import { debugLog } from "../debug/logging.js";

function cloneMonzo(monzo) {
  return Array.isArray(monzo) ? [...monzo] : null;
}

function displacedFraction(ratio, equaveRatio, equavePower) {
  if (!ratio?.toFraction) return null;
  if (!equavePower || !equaveRatio?.pow) return ratio;
  return equavePower > 0
    ? ratio.mul(equaveRatio.pow(equavePower))
    : ratio.div(equaveRatio.pow(Math.abs(equavePower)));
}

function scaleIdentityForDegree(keys, degree) {
  const scaleLength = keys.tuning.scale?.length ?? 0;
  if (!scaleLength || !Number.isFinite(degree)) return null;
  const reducedDegree = ((degree % scaleLength) + scaleLength) % scaleLength;
  const equavePower = Math.floor(degree / scaleLength);
  const interval = keys.tuning.degreeIntervals?.[reducedDegree] ?? null;
  const ratio = displacedFraction(interval?.ratio, keys.tuning.equaveInterval?.ratio, equavePower);
  if (!ratio?.toFraction) return null;

  const ratioText = ratio.toFraction();
  const baseMonzo = cloneMonzo(interval?.monzo);
  const equaveMonzo = cloneMonzo(keys.tuning.equaveInterval?.monzo);
  const monzo =
    baseMonzo && equaveMonzo
      ? baseMonzo.map((value, index) => value + equavePower * (equaveMonzo[index] ?? 0))
      : baseMonzo;
  return {
    ratioText: ratioText.includes("/") ? ratioText : `${ratioText}/1`,
    monzo,
  };
}

export function midiLatchToggle(keys, coords, releaseVelocity = 0) {
  if (!keys.state.latch) return false;
  const removed = removeSustainedHex(keys.state, coords);
  if (!removed) return false;
  const [hex, vel] = removed.entry;
  hex.noteOff(releaseVelocity || vel);
  keys._scheduleDeferredBulkRefresh();
  hexOff(keys, coords);
  keys._emitLiveNoteDisplayState();
  return true;
}

export function maybeTakeOverModulationTarget(keys, coords, cents, cents_prev, cents_next) {
  if (keys._modulationState.mode !== "pending_settlement") return null;
  if (keys._modulationState.lastDecision?.articulation !== "takeover") return null;
  if (keys._modulationState.takeoverConsumed) return null;
  const sourceHex = keys._modulationState.sourceHex;
  if (!sourceHex || !keys._isHexStillSounding(sourceHex)) return null;

  const noteContext = keys._createNoteContext(frameForNewNotes(keys._modulationState));
  const onsetFrameId = noteContext?.frameId ?? keys._harmonicFrame?.id ?? null;
  const proxy = createTransferredHex(sourceHex, {
    coords,
    cents,
    cents_prev,
    cents_next,
    noteContext,
    onsetFrameId,
  });
  keys._modulationState.takeoverConsumed = true;
  keys.recencyStack.remove(sourceHex);
  keys.recencyStack.push(proxy);
  keys._updateWheelTarget();
  keys._applyCurrentWheelToHex(proxy);
  keys._syncTransferredWheelBend(proxy);
  return proxy;
}

export function hexOn(keys, coords, note_played, velocity_played, bend, options = null) {
  keys._markSoundActivity();
  keys._lastHexOnSuppressed = false;
  const modulationCommit = keys._commitPendingModulationTarget(coords, options ?? {});
  if (modulationCommit?.suppressNoteOn) {
    keys._lastHexOnSuppressed = true;
    return null;
  }
  if (keys._staticDeferredBulkActive && keys._deferredBulkMapRefresh) {
    keys._sendBulkDumpOctaveRefresh(keys._hasSoundingNotes(), false);
    keys._deferredBulkMapRefresh = false;
    if (!keys._hasSoundingNotes()) keys._staticDeferredBulkActive = false;
  }

  if (!bend) bend = 0;
  if (!velocity_played) velocity_played = keys.settings.midi_velocity;
  if (!velocity_played) velocity_played = 72;

  const pitchAtCoords =
    typeof keys.hexCoordsToLiveCents === "function"
      ? keys.hexCoordsToLiveCents(coords)
      : keys.hexCoordsToCents(coords);
  const [cents, pressed_interval, steps, equaves, equivSteps, cents_prev, cents_next] =
    pitchAtCoords;
  keys._lastPlayedDegree = pressed_interval ?? keys._lastPlayedDegree;
  const [color, text_color] = keys.centsToColor(cents, true, pressed_interval);
  keys.drawHex(coords, color, text_color);

  const transferredHex = maybeTakeOverModulationTarget(keys, coords, cents, cents_prev, cents_next);
  if (transferredHex) return transferredHex;

  const degree0toRef_ratio = keys.tuning.degree0toRef_asArray[1];
  const noteContext = keys._createNoteContext(frameForNewNotes(keys._modulationState));
  if (noteContext) {
    noteContext.displayLabel = keys.getDisplayLabelAtCoords(coords, {
      frame: noteContext.frame,
      geometryMode: noteContext.geometryMode,
    });
    const scaleIdentity = scaleIdentityForDegree(keys, steps);
    if (scaleIdentity) {
      noteContext.scaleRatioText = scaleIdentity.ratioText;
      noteContext.scaleMonzo = scaleIdentity.monzo;
    }
  }
  const hex = keys.synth.makeHex(
    coords,
    cents,
    steps,
    equaves,
    equivSteps,
    cents_prev,
    cents_next,
    note_played,
    velocity_played,
    bend,
    degree0toRef_ratio,
  );
  debugLog("osc", "LiveHex.hexOn", {
    synthFamily: keys.synth?.family,
    coords,
    note_played,
    velocity_played,
    bend,
    liveInputAddress: options?.liveInputAddress ?? null,
    rasterGenerated: options?.rasterGenerated === true,
  });
  hex._baseCents = cents;
  hex._noteContext = noteContext;
  if (options?.liveInputAddress) hex._liveInputAddress = { ...options.liveInputAddress };
  if (options?.rasterGenerated === true) hex._rasterGenerated = true;

  let mpePrimedBeforeNoteOn = false;
  const inputChannel = options?.liveInputAddress?.channel;
  if (
    inputChannel != null &&
    (keys.inputRuntime.mpeInput || keys.inputRuntime.perChannelExpression)
  ) {
    const bend14 = keys._mpeInputBendByChannel.get(inputChannel);
    const bend21 = keys._hakenMpeBend21ByChannel.get(inputChannel);
    if (keys.inputRuntime.mpeInput && keys.inputRuntime.target === "scale" && bend14 != null) {
      hex._scaleModeBendAnchor14 = keys._normalizePitchBend14(bend14);
      hex._scaleModeBendAnchor21 = bend21 ?? null;
      hex._mpePrimedBeforeNoteOn = {
        channel: inputChannel,
        bend14,
        bend21: bend21 ?? null,
        bentCents: cents,
      };
      mpePrimedBeforeNoteOn = true;
    } else if (bend14 != null && bend14 !== 8192 && hex.retune) {
      let norm = (keys._normalizePitchBend14(bend14) - 8192) / 8192;
      if (keys.inputRuntime.bendFlip) norm = -norm;
      const bentCents = cents + norm * scalaToCents(keys.inputRuntime.bendRange ?? "9/8");
      hex.retune(bentCents, true);
      hex._mpePrimedBeforeNoteOn = {
        channel: inputChannel,
        bend14,
        bend21: bend21 ?? null,
        bentCents,
      };
      mpePrimedBeforeNoteOn = true;
    }
  }

  let wheelPrimedBeforeNoteOn = false;
  if (!keys.inputRuntime.wheelToRecent && keys._wheelValue14 !== 8192) {
    const wheelTargetCents = cents + keys._wheelBend;
    if (hex.standardWheelRetune) {
      hex.standardWheelRetune(wheelTargetCents);
      wheelPrimedBeforeNoteOn = !hex.standardWheelPassthroughOnly;
    } else if (hex.retune && !hex.standardWheelPassthroughOnly) {
      hex.retune(wheelTargetCents, true);
      wheelPrimedBeforeNoteOn = true;
    }
  }
  if (wheelPrimedBeforeNoteOn) hex._wheelPrimedBeforeNoteOn = true;
  hex.noteOn();
  hex._onsetFrameId =
    noteContext?.frameId ??
    frameForNewNotes(keys._modulationState)?.id ??
    keys._harmonicFrame?.id ??
    null;
  hex.cents_prev = cents_prev;
  hex.cents_next = cents_next;
  keys.recencyStack.push(hex);
  if (
    keys._modulationState.mode === "awaiting_target" &&
    keys._modulationState.sourceCoordsKey === `${coords.x},${coords.y}`
  ) {
    keys._setAwaitingModulationSource(hex, pressed_interval, coords);
  }
  keys._updateWheelTarget();
  delete hex._wheelPrimedBeforeNoteOn;
  if (!wheelPrimedBeforeNoteOn) keys._applyCurrentWheelToHex(hex);
  if (!mpePrimedBeforeNoteOn) delete hex._mpePrimedBeforeNoteOn;
  keys._emitLiveNoteDisplayState();
  return hex;
}

export function hexOff(keys, coords) {
  const [cents, pressed_interval] = keys.hexCoordsToCents(coords);
  const key = coords.x + "," + coords.y;
  const isSustained = keys.state.sustainedCoords.has(key);
  const isActiveElsewhere = keys._isCoordActive(coords);
  const [color, text_color] = keys.centsToColor(
    cents,
    isSustained || isActiveElsewhere,
    pressed_interval,
  );
  // Release redraw should be immediate and local: repaint the released hex in
  // its current unpressed/held state rather than restoring a cached rectangle
  // and then repairing overlapping sounding-note overlays.
  keys.drawHex(coords, color, text_color);
}

export function noteOff(keys, hex, release_velocity) {
  keys._markSoundActivity();
  debugLog("osc", "LiveHex.noteOff", {
    synthFamily: keys.synth?.family,
    coords: hex?.coords,
    releaseVelocity: release_velocity,
    released: hex?.release,
  });
  if (shouldSuppressTransferredSourceRelease(hex)) {
    releaseTransferredSourceExpression(hex);
    keys.recencyStack.remove(hex);
    keys._updateWheelTarget(true);
    keys._syncAwaitingModulationSource();
    keys._emitLiveNoteDisplayState();
    return;
  }
  if (keys.state.sustain) {
    const result = addSustainedHex(keys.state, hex, release_velocity);
    if (result.added) {
      keys._drawSoundingHex(hex);
    }
    keys._emitLiveNoteDisplayState();
    return;
  }

  if (keys._deferredBulkMapRefresh && !keys._staticDeferredBulkActive) {
    keys._sendBulkDumpOctaveRefresh(true);
  }
  hex.noteOff(release_velocity);
  keys._trackRecentlyReleasedHex(hex);
  keys.recencyStack.remove(hex);
  keys._updateWheelTarget(true);
  keys._syncAwaitingModulationSource();
  if (keys._staticDeferredBulkActive) {
    keys._deferredBulkMapRefresh = keys._hasSoundingNotes();
    if (!keys._deferredBulkMapRefresh) keys._staticDeferredBulkActive = false;
  } else {
    keys._scheduleDeferredBulkRefresh();
  }
  keys._settleModulationAfterActiveRelease();
  keys._emitLiveNoteDisplayState();
}

export function sustainOff(keys, force = false) {
  if (keys.state.latch && !force) return;
  if (keys.state.latch) keys.state.latch = false;
  keys.state.sustain = false;
  const notesToRelease = clearSustainedHexes(keys.state);
  if (
    keys._deferredBulkMapRefresh &&
    !keys._staticDeferredBulkActive &&
    notesToRelease.length > 0
  ) {
    keys._sendBulkDumpOctaveRefresh(true);
  }
  for (let note = 0; note < notesToRelease.length; note++) {
    const hex = notesToRelease[note][0];
    const [cents, pressed_interval] = keys.hexCoordsToCents(hex.coords);
    if (keys._isHexActivelyHeld(hex)) {
      keys._drawSoundingHex(hex);
      continue;
    }
    const [color, text_color] = keys.centsToColor(cents, false, pressed_interval);
    keys.drawHex(hex.coords, color, text_color);
    hex.noteOff(notesToRelease[note][1]);
    keys._trackRecentlyReleasedHex(hex);
    keys.recencyStack.remove(hex);
  }
  keys._updateWheelTarget(true);
  keys._syncAwaitingModulationSource();
  if (keys._staticDeferredBulkActive) {
    keys._deferredBulkMapRefresh = keys._hasSoundingNotes();
    if (!keys._deferredBulkMapRefresh) keys._staticDeferredBulkActive = false;
  } else {
    keys._scheduleDeferredBulkRefresh();
  }
  keys._settleModulationAfterActiveRelease();
  if (keys.onLatchChange) keys.onLatchChange(false);
  keys._emitLiveNoteDisplayState();
}

export function sustainOn(keys) {
  keys.state.sustain = true;
}

export function latchToggle(keys) {
  if (keys.state.latch) {
    keys.state.latch = false;
    sustainOff(keys, true);
    return;
  }
  keys.state.latch = true;
  keys.state.sustain = true;
  if (keys.onLatchChange) keys.onLatchChange(true);
  for (const hex of keys._allActiveHexes()) {
    if (!keys.state.sustainedNotes.find(([h]) => h === hex)) {
      keys.state.sustainedNotes.push([hex, 0]);
      keys.state.sustainedCoords.add(hex.coords.x + "," + hex.coords.y);
    }
  }
}
